import { NOTION_COLUMN_COLORS, type NotionColumnColor } from "./notion";

/**
 * WSL Notion board properties and views (migration 0050), Notion's database
 * properties for a kanban board: a board defines its own fields (text,
 * number, select, multi-select, date, person, checkbox, URL, email, phone),
 * every card holds a value for each, and the board's ONE shared view
 * decides how cards are filtered and sorted and which fields show on them.
 *
 * Wire shapes and limits only; the API validates every value against the
 * property's type (`checkPropertyValue` is shared by both sides).
 */

export const NOTION_PROPERTY_TYPES = ["TEXT", "NUMBER", "SELECT", "MULTI_SELECT", "DATE", "PERSON", "CHECKBOX", "URL", "EMAIL", "PHONE"] as const;
export type NotionPropertyType = (typeof NOTION_PROPERTY_TYPES)[number];

export const NOTION_PROPERTIES_MAX = 30;

/**
 * Changing a property's type (user, 2026-10-23). These changes KEEP each
 * card's value, converted: select ↔ multi-select (a value becomes a one-item
 * list; a list keeps its first), and number / URL / email / phone → text.
 * Every other change CLEARS the property on every card — the editor shows
 * how many first (`GET …/properties/:id/type-impact`).
 */
export function propertyTypeChangeKeeps(from: NotionPropertyType, to: NotionPropertyType): boolean {
	if (from === to) return true;
	if ((from === "SELECT" && to === "MULTI_SELECT") || (from === "MULTI_SELECT" && to === "SELECT")) return true;
	return to === "TEXT" && (from === "NUMBER" || from === "URL" || from === "EMAIL" || from === "PHONE");
}

export interface NotionPropertyTypeImpact {
	/** Cards holding a value for the property now. */
	cards_with_value: number;
	/** True = they keep it (converted); false = it is cleared from every one of them. */
	keeps: boolean;
}
export const NOTION_PROPERTY_NAME_MAX = 60;
export const NOTION_PROPERTY_OPTIONS_MAX = 100;
export const NOTION_PROPERTY_OPTION_NAME_MAX = 60;
export const NOTION_PROPERTY_TEXT_MAX = 2000;
export const NOTION_PROPERTY_PHONE_MAX = 40;
/** Option colours are the column colours. */
export const NOTION_OPTION_COLORS = NOTION_COLUMN_COLORS;

export interface NotionPropertyOption {
	id: string;
	name: string;
	color: NotionColumnColor;
}

export interface NotionBoardProperty {
	id: string;
	name: string;
	type: NotionPropertyType;
	/** SELECT / MULTI_SELECT only; [] for the rest. In the order they are offered. */
	options: NotionPropertyOption[];
	/** Shown on the card face (always shown in the open card). */
	visible: boolean;
	version: number;
}

/**
 * A card's value for one property: a string (text, URL, email, phone, a
 * select's option id, a date `YYYY-MM-DD`, a person's user id), a number,
 * true (a ticked checkbox), or option ids (multi-select). No value = absent.
 */
export type NotionPropertyValue = string | number | boolean | string[];

/** A card's values, by property id (absent = empty). */
export type NotionCardProperties = Record<string, NotionPropertyValue>;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+0-9 ()./-]+$/;

/**
 * Is `v` a valid value for this property, and what is stored? Returns the
 * cleaned value, null (= clear it), or "invalid". People are checked for
 * membership by the server, on top of this.
 */
export function checkPropertyValue(p: Pick<NotionBoardProperty, "type" | "options">, v: unknown): NotionPropertyValue | null | "invalid" {
	if (v === null || v === undefined) return null;
	switch (p.type) {
		case "TEXT": {
			if (typeof v !== "string") return "invalid";
			const s = v.trim();
			return s === "" ? null : s.length > NOTION_PROPERTY_TEXT_MAX ? "invalid" : s;
		}
		case "NUMBER":
			return typeof v === "number" && Number.isFinite(v) && Math.abs(v) < 1e15 ? v : "invalid";
		case "SELECT":
			return typeof v === "string" && p.options.some((o) => o.id === v) ? v : "invalid";
		case "MULTI_SELECT": {
			if (!Array.isArray(v) || !v.every((x) => typeof x === "string" && p.options.some((o) => o.id === x))) return "invalid";
			const ids = [...new Set(v as string[])];
			return ids.length === 0 ? null : ids;
		}
		case "DATE":
			return typeof v === "string" && DAY.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) ? v : "invalid";
		case "PERSON":
			return typeof v === "string" && /^[0-9a-f-]{36}$/i.test(v) ? v : "invalid";
		case "CHECKBOX":
			return v === true ? true : v === false ? null : "invalid";
		case "URL": {
			if (typeof v !== "string") return "invalid";
			const s = v.trim();
			if (s === "") return null;
			return s.length <= NOTION_PROPERTY_TEXT_MAX && /^https?:\/\/\S+$/i.test(s) ? s : "invalid";
		}
		case "EMAIL": {
			if (typeof v !== "string") return "invalid";
			const s = v.trim();
			return s === "" ? null : s.length <= 254 && EMAIL.test(s) ? s : "invalid";
		}
		case "PHONE": {
			if (typeof v !== "string") return "invalid";
			const s = v.trim();
			return s === "" ? null : s.length <= NOTION_PROPERTY_PHONE_MAX && PHONE.test(s) ? s : "invalid";
		}
	}
}

// ── The board view (filters, sort, card face) ───────────────────────────────

/** What a filter or a sort reads: a built-in field, or a property (`property_id`). */
export const NOTION_BOARD_FIELDS = ["TITLE", "ASSIGNEE", "DUE_DATE", "LABELS", "CREATED_AT", "UPDATED_AT", "PROPERTY"] as const;
export type NotionBoardField = (typeof NOTION_BOARD_FIELDS)[number];

export const NOTION_FILTER_OPS = [
	"CONTAINS",
	"NOT_CONTAINS",
	"IS",
	"IS_NOT",
	"IS_EMPTY",
	"IS_NOT_EMPTY",
	"GT",
	"LT",
	"GTE",
	"LTE",
	"BEFORE",
	"AFTER",
	"IS_CHECKED",
	"IS_NOT_CHECKED",
] as const;
export type NotionFilterOp = (typeof NOTION_FILTER_OPS)[number];

export const NOTION_BOARD_FILTERS_MAX = 10;

/** What a filter compares, by what it reads: one list, used by the API to refuse and the app to offer. */
export type NotionFilterKind = "text" | "number" | "select" | "multi" | "date" | "person" | "checkbox" | "labels";

export function filterKindOf(field: NotionBoardField, type?: NotionPropertyType): NotionFilterKind | null {
	switch (field) {
		case "TITLE":
			return "text";
		case "ASSIGNEE":
			return "person";
		case "DUE_DATE":
			return "date";
		case "LABELS":
			return "labels";
		case "CREATED_AT":
		case "UPDATED_AT":
			return null;
		case "PROPERTY":
			switch (type) {
				case "TEXT":
				case "URL":
				case "EMAIL":
				case "PHONE":
					return "text";
				case "NUMBER":
					return "number";
				case "SELECT":
					return "select";
				case "MULTI_SELECT":
					return "multi";
				case "DATE":
					return "date";
				case "PERSON":
					return "person";
				case "CHECKBOX":
					return "checkbox";
				default:
					return null;
			}
	}
}

export const NOTION_FILTER_OPS_BY_KIND: Record<NotionFilterKind, readonly NotionFilterOp[]> = {
	text: ["CONTAINS", "NOT_CONTAINS", "IS", "IS_NOT", "IS_EMPTY", "IS_NOT_EMPTY"],
	number: ["IS", "IS_NOT", "GT", "LT", "GTE", "LTE", "IS_EMPTY", "IS_NOT_EMPTY"],
	select: ["IS", "IS_NOT", "IS_EMPTY", "IS_NOT_EMPTY"],
	multi: ["CONTAINS", "NOT_CONTAINS", "IS_EMPTY", "IS_NOT_EMPTY"],
	date: ["IS", "BEFORE", "AFTER", "IS_EMPTY", "IS_NOT_EMPTY"],
	person: ["IS", "IS_NOT", "IS_EMPTY", "IS_NOT_EMPTY"],
	checkbox: ["IS_CHECKED", "IS_NOT_CHECKED"],
	labels: ["CONTAINS", "NOT_CONTAINS", "IS_EMPTY", "IS_NOT_EMPTY"],
};

/** Ops that take no value. */
export const NOTION_FILTER_OPS_NO_VALUE: readonly NotionFilterOp[] = ["IS_EMPTY", "IS_NOT_EMPTY", "IS_CHECKED", "IS_NOT_CHECKED"];

export interface NotionBoardFilter {
	field: NotionBoardField;
	property_id: string | null;
	op: NotionFilterOp;
	/** Text, a number, a date, an option / user id, a label — by kind. Null for the no-value ops. */
	value: string | number | null;
}

/** Fields a board can be sorted by (a property: TEXT, NUMBER, SELECT, DATE, CHECKBOX, URL, EMAIL, PHONE, PERSON). */
export const NOTION_SORT_FIELDS = ["MANUAL", "TITLE", "DUE_DATE", "CREATED_AT", "UPDATED_AT", "PROPERTY"] as const;
export type NotionSortField = (typeof NOTION_SORT_FIELDS)[number];
export const NOTION_SORT_DIRECTIONS = ["ASC", "DESC"] as const;
export type NotionSortDirection = (typeof NOTION_SORT_DIRECTIONS)[number];

/** Built-in fields the card face may show. */
export const NOTION_CARD_FACE_FIELDS = ["ASSIGNEE", "DUE_DATE", "LABELS"] as const;
export type NotionCardFaceField = (typeof NOTION_CARD_FACE_FIELDS)[number];

export interface NotionBoardView {
	/** Every filter must hold (AND). */
	filters: NotionBoardFilter[];
	sort: { field: NotionSortField; property_id: string | null; direction: NotionSortDirection };
	/** Built-in fields on the card face (properties say so themselves: `visible`). */
	card_fields: NotionCardFaceField[];
}

export const NOTION_DEFAULT_BOARD_VIEW: NotionBoardView = {
	filters: [],
	sort: { field: "MANUAL", property_id: null, direction: "ASC" },
	card_fields: ["ASSIGNEE", "DUE_DATE", "LABELS"],
};

/** Can a property be a sort key? (A multi-select has no single value to order by.) */
export const isSortableProperty = (type: NotionPropertyType): boolean => type !== "MULTI_SELECT";

/**
 * "Group by" (user, 2026-10-30): a board's columns by assignee or by a
 * property instead of by status — each viewer's own choice, sent as `group`
 * on the read and never stored on the board. `NOTION_GROUP_ASSIGNEE` or a
 * property id of one of these types. A group's key is the person / option id,
 * or `NOTION_GROUP_NONE` for the cards with no value.
 */
export const NOTION_GROUPABLE_TYPES = ["SELECT", "MULTI_SELECT", "PERSON"] as const satisfies readonly NotionPropertyType[];
export const isGroupableProperty = (type: NotionPropertyType): boolean => (NOTION_GROUPABLE_TYPES as readonly NotionPropertyType[]).includes(type);
export const NOTION_GROUP_ASSIGNEE = "assignee";
export const NOTION_GROUP_NONE = "none";

/** Card search (the board's own search box, not stored): matches the title. */
export const NOTION_BOARD_SEARCH_MAX = 100;
